/**
 * PackingMaterialsTab — 5th tab in Inventory Dashboard.
 * Lists all packing material lots (is_packing_material = 'Y'),
 * with summary stats, search/filter, pagination, and linked products.
 */
import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Search, Loader2 } from 'lucide-react';
import inventoryService, { Lot, PackingLinkedProduct } from '../../services/inventoryService';
import { useLanguage } from '../../i18n/LanguageContext';
import { formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import { getStatusBadge, getStatusDisplayName as getStatusDisplayNameBase } from './inventoryHelpers';

interface PackingMaterialsTabProps {
  openScanDetails: (lot: Lot) => void;
}

const PackingMaterialsTab: React.FC<PackingMaterialsTabProps> = ({
  openScanDetails,
}) => {
  const { t } = useLanguage();
  const statusDisplayName = (status: string, approvalStatus?: string) => getStatusDisplayNameBase(status, t, approvalStatus);
  const formatDateTime = (dateString?: string) => formatDateTimeUtil(dateString);

  const [packingSearch, setPackingSearch] = useState('');
  const [packingStatus, setPackingStatus] = useState('');
  const [packingPage, setPackingPage] = useState(1);

  // Linked products cache: itemNumber -> products[]
  const [linkedProductsMap, setLinkedProductsMap] = useState<Record<string, PackingLinkedProduct[]>>({});

  // Fetch packing material lots
  const { data: lotsData, isLoading: lotsLoading } = useQuery(
    ['packingLots', packingSearch, packingStatus, packingPage],
    () => inventoryService.getLots({
      page: packingPage,
      limit: 20,
      search: packingSearch || undefined,
      status: packingStatus || undefined,
      is_packing_material: 'Y',
    }),
    { refetchInterval: 30000, keepPreviousData: true }
  );

  // Fetch linked products for each unique item_number in current page
  useEffect(() => {
    if (!lotsData?.lots) return;
    const uniqueItems = [...new Set(lotsData.lots.map(l => l.item_number))];
    const missing = uniqueItems.filter(item => !(item in linkedProductsMap));
    if (missing.length === 0) return;

    missing.forEach(async (itemNumber) => {
      try {
        const products = await inventoryService.getPackingLinkedProducts(itemNumber);
        setLinkedProductsMap(prev => ({ ...prev, [itemNumber]: products }));
      } catch {
        setLinkedProductsMap(prev => ({ ...prev, [itemNumber]: [] }));
      }
    });
  }, [lotsData?.lots]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
          <input
            type="text"
            placeholder={t('common.search')}
            value={packingSearch}
            onChange={(e) => { setPackingSearch(e.target.value); setPackingPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
        <select
          value={packingStatus}
          onChange={(e) => { setPackingStatus(e.target.value); setPackingPage(1); }}
          className="px-4 py-2 border border-secondary-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="">{t('inventory.allStatuses')}</option>
          <option value="available">{t('inventory.statusAvailable')}</option>
          <option value="reserved">{t('inventory.statusReserved') || 'Reserved'}</option>
          <option value="depleted">{t('inventory.statusDepleted') || 'Depleted'}</option>
        </select>
      </div>

      {/* Table */}
      {lotsLoading ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          {t('common.loading')}
        </div>
      ) : lotsData?.lots && lotsData.lots.length > 0 ? (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.itemNumber')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.itemName')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.lotNumber')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('transferVerification.qty')}</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">PCS</th>
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('common.status')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.linkedProducts')}</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider whitespace-nowrap">{t('inventory.updatedAt')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {lotsData.lots.map((lot: Lot, index: number) => {
                    const linked = linkedProductsMap[lot.item_number];
                    return (
                      <tr key={`${lot.id}-${lot.transfer_number}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer" onClick={() => openScanDetails(lot)}>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                          {lot.item_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                          <div>{lot.item_name || '-'}</div>
                          {lot.chinese_name && (
                            <div className="text-xs text-gray-500 dark:text-gray-400">{lot.chinese_name}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {lot.transfer_number}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                          {Math.round(Number(lot.actual_quantity || 0))}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-600 dark:text-gray-300">
                          {Number(lot.item_sub_quantity || 0) > 0
                            ? Math.round(Number(lot.actual_quantity || 0)) * Math.round(Number(lot.item_sub_quantity))
                            : '-'}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(lot.status, lot.approval_status)}`}>
                            {statusDisplayName(lot.status, lot.approval_status)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-[200px]">
                          {linked === undefined ? (
                            <span className="text-gray-400 text-xs">...</span>
                          ) : linked.length > 0 ? (
                            <span className="text-xs truncate block" title={linked.map(p => p.product_name || p.product_item_number).join(', ')}>
                              {linked.map(p => p.product_name || p.product_item_number).join(', ')}
                            </span>
                          ) : (
                            <span className="text-gray-400 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300">
                          {lot.verified_at ? formatDateTime(lot.verified_at) : lot.updated_at ? formatDateTime(lot.updated_at) : formatDateTime(lot.received_date)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {lotsData.pagination.total_pages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {t('common.showing')} {((packingPage - 1) * 20) + 1} - {Math.min(packingPage * 20, lotsData.pagination.total)} {t('common.of')} {lotsData.pagination.total}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPackingPage(p => Math.max(1, p - 1))}
                    disabled={packingPage === 1}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
                  >
                    {t('common.previous')}
                  </button>
                  <span className="px-3 py-1 text-sm text-gray-600 dark:text-gray-300">
                    {packingPage} / {lotsData.pagination.total_pages}
                  </span>
                  <button
                    onClick={() => setPackingPage(p => Math.min(lotsData.pagination.total_pages, p + 1))}
                    disabled={packingPage === lotsData.pagination.total_pages}
                    className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 dark:bg-gray-800"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">{t('messages.noData')}</div>
      )}
    </>
  );
};

export default PackingMaterialsTab;
